import { useState, useEffect, useRef } from 'react';
import { UpgradeModal } from './UpgradeModal';
import { useConversionContext } from '../../contexts/ConversionTriggersContext';

// ── PlanLimitAlert ─────────────────────────────────────────────────────────────
// Shows a progressional warning based on usage percentage:
//   < 70%  → nothing
//   70–79% → subtle soft hint (gray)
//   80–89% → amber warning
//   90–99% → orange warning (near-limit)
//   100%   → red hard limit

interface PlanLimitAlertProps {
  current: number;
  /** null = ilimitado — componente retorna null silenciosamente */
  limit: number | null;
  resource: string;
  onUpgrade: () => void;
  className?: string;
}

export const PlanLimitAlert = ({ current, limit, resource, onUpgrade, className = '' }: PlanLimitAlertProps) => {
  // ── All hooks MUST be called before any early return ─────────────────────
  const { trackLimitHit } = useConversionContext();
  const trackedRef = useRef(false);

  const safeLimit = (limit === null || limit === undefined || limit < 0) ? 0 : limit;
  const isUnlimited = limit === null || limit === undefined || limit < 0;
  const isAtLimit = !isUnlimited && current >= safeLimit;
  const percentage = safeLimit > 0 ? Math.min((current / safeLimit) * 100, 100) : 0;

  const isHardLimit = isAtLimit;
  const isNearLimit = percentage >= 90 && !isHardLimit;
  const isWarning   = percentage >= 80 && !isNearLimit && !isHardLimit;
  const isSoftHint  = percentage >= 70 && !isWarning && !isNearLimit && !isHardLimit;

  useEffect(() => {
    if (!trackedRef.current && !isUnlimited && (isHardLimit || isNearLimit)) {
      trackedRef.current = true;
      trackLimitHit(resource);
    }
  }, [isHardLimit, isNearLimit, isUnlimited, resource, trackLimitHit]);

  // ── Early returns AFTER all hooks ─────────────────────────────────────────
  if (isUnlimited) return null;
  if (!isSoftHint && !isWarning && !isNearLimit && !isHardLimit) return null;

  // Styles per severity
  const style = isHardLimit
    ? { bg: 'bg-red-50 border-red-200',   iconBg: 'bg-red-100',    iconColor: 'text-red-500',    textColor: 'text-red-700',    subColor: 'text-red-500',    btnClass: 'bg-red-500 hover:bg-red-600 text-white',    barColor: 'bg-red-500' }
    : isNearLimit
    ? { bg: 'bg-orange-50 border-orange-200', iconBg: 'bg-orange-100', iconColor: 'text-orange-500', textColor: 'text-orange-700', subColor: 'text-orange-500', btnClass: 'bg-orange-500 hover:bg-orange-600 text-white', barColor: 'bg-orange-400' }
    : isWarning
    ? { bg: 'bg-amber-50 border-amber-200',   iconBg: 'bg-amber-100',   iconColor: 'text-amber-500',  textColor: 'text-amber-700',  subColor: 'text-amber-500',  btnClass: 'bg-amber-500 hover:bg-amber-600 text-white',  barColor: 'bg-amber-400' }
    : { bg: 'bg-gray-50 border-gray-200',     iconBg: 'bg-gray-100',    iconColor: 'text-gray-400',   textColor: 'text-gray-600',   subColor: 'text-gray-400',   btnClass: 'bg-gray-500 hover:bg-gray-600 text-white',   barColor: 'bg-gray-300' };

  const label = isHardLimit
    ? `Limite de ${resource} atingido`
    : isNearLimit
    ? `Quase no limite de ${resource}`
    : isWarning
    ? `Usando ${resource} com frequência`
    : `Acompanhe o uso de ${resource}`;

  const sub = isHardLimit
    ? `${current} de ${safeLimit} ${resource} utilizados — upgrade para continuar`
    : `${current} de ${safeLimit} ${resource} utilizados (${Math.round(percentage)}%)`;

  return (
    <div className={`rounded-xl border px-4 py-3 ${style.bg} ${className}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${style.iconBg}`}>
            <i className={`${isHardLimit ? 'ri-lock-line' : 'ri-error-warning-line'} text-base ${style.iconColor}`} />
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-600 ${style.textColor}`}>{label}</p>
            <p className={`text-xs mt-0.5 ${style.subColor}`}>{sub}</p>
          </div>
        </div>
        {(isWarning || isNearLimit || isHardLimit) && (
          <button
            onClick={onUpgrade}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-600 transition-colors cursor-pointer whitespace-nowrap ${style.btnClass}`}
          >
            <i className="ri-vip-crown-line" />
            Upgrade
          </button>
        )}
      </div>
      {/* Progress bar */}
      <div className="mt-2.5 h-1.5 bg-black/5 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${style.barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// ── PremiumBadge ───────────────────────────────────────────────────────────────
interface PremiumBadgeProps {
  className?: string;
}

export const PremiumBadge = ({ className = '' }: PremiumBadgeProps) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-600 rounded-full ${className}`}>
    <i className="ri-vip-crown-line text-xs" />
    Pro
  </span>
);

// ── PremiumBlocker ─────────────────────────────────────────────────────────────
interface PremiumBlockerProps {
  feature: string;
  onUpgrade: () => void;
}

export const PremiumBlocker = ({ feature, onUpgrade }: PremiumBlockerProps) => (
  <div className="relative rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-6 flex flex-col items-center justify-center text-center min-h-[160px]">
    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
      <i className="ri-lock-line text-amber-500 text-lg" />
    </div>
    <p className="text-sm font-600 text-gray-700 mb-1">{feature}</p>
    <p className="text-xs text-gray-400 mb-3">Disponível no plano Pago</p>
    <button
      onClick={onUpgrade}
      className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
    >
      <i className="ri-vip-crown-line" />
      Fazer upgrade
    </button>
  </div>
);

// ── ContextualUpgradeInline ────────────────────────────────────────────────────
// Small inline card showing upgrade value for a specific blocked context
interface ContextualUpgradeInlineProps {
  title: string;
  message: string;
  ctaLabel?: string;
  icon?: string;
}

export const ContextualUpgradeInline = ({ title, message, ctaLabel = 'Fazer upgrade', icon = 'ri-vip-crown-line' }: ContextualUpgradeInlineProps) => {
  const [showUpgrade, setShowUpgrade] = useState(false);

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-xl">
        <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
          <i className={`${icon} text-emerald-600 text-sm`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-700 text-gray-800">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{message}</p>
        </div>
        <button
          onClick={() => setShowUpgrade(true)}
          className="shrink-0 flex items-center gap-1.5 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-700 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-arrow-right-up-line text-xs" />
          {ctaLabel}
        </button>
      </div>
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title={title}
        description={message}
        trigger="feature_block"
      />
    </>
  );
};
